import type { ReactNode } from 'react';
import { Brand, ThemeToggle } from '@efira/ui';

import { LANDING_URL } from '../config';

/**
 * Обёртка экранов auth на блоке реестра `login-02` (единица 19).
 *
 * ЧТО ВЗЯТО ИЗ БЛОКА: две колонки, форма слева с логотипом в углу,
 * изображение справа во всю высоту, порог `lg` для второй колонки,
 * ширина формы, вертикальный ритм.
 *
 * ЧТО НАШЕ: содержимое формы целиком (её приносит экран), переключатель
 * темы, обложка, тексты. Демонстрационного «Acme Inc.» и
 * `/placeholder.svg` из блока не остаётся - это сторожат ворота
 * (`e2e/auth.spec.ts`, «в продукте нет демо-содержимого реестра»).
 *
 * ЗАЧЕМ ЗАМЕНА. Находка аудита 05: центрированная карточка формы на
 * пустом белом поле шириной 1470 px - ни строки о продукте, ни одного
 * изображения, ни одной причины дозаполнить форму. Такой экран можно
 * без правок отдать любому SaaS. Решение владельца 2026-09-27 (1) и (7):
 * две колонки с обложкой, обложка - коллаж каталога.
 *
 * ВСЕ ПЯТЬ ЭКРАНОВ auth на этом шаблоне: вход, регистрация,
 * восстановление, подтверждение почты, сброс. Форма у каждого своя,
 * рама одна - иначе «одна роль - один вид» держалось бы на внимании.
 *
 * НА ТЕЛЕФОНЕ обложка уходит (`lg:block`), форма остаётся и прижата к
 * верху: клавиатура поднимается снизу, и форма по центру уезжала бы под
 * неё.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex items-center justify-between gap-4">
          <Brand href={LANDING_URL} />
          <ThemeToggle />
        </div>

        <main className="flex flex-1 items-start justify-center pt-4 sm:items-center sm:pt-0">
          <div className="flex w-full max-w-sm flex-col gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-bold">{title}</h1>
              {subtitle === undefined ? null : (
                <p className="text-foreground/80 text-sm text-balance">{subtitle}</p>
              )}
            </div>

            {children}

            {footer === undefined ? null : (
              <p className="text-foreground/80 text-center text-sm">{footer}</p>
            )}
          </div>
        </main>
      </div>

      {/*
        ОБЛОЖКА - ОДИН ФАЙЛ, А НЕ ЖИВАЯ СЕТКА КАРТОЧЕК.
        Решение владельца 2026-09-27 (7). Коллаж собран из плиток
        каталога инструментом `tools/covers/collage.mjs`: десять плиток
        разметкой - это десять запросов и десять шансов увидеть дыру у
        человека, который ещё не зарегистрировался. Вес под воротами
        `pnpm covers:check` отдельной строкой.

        `aria-hidden` НЕ СТАВИТСЯ, а подпись говорит по смыслу: для того,
        кто не видит экран, это единственный способ узнать, что справа
        показывают игры продукта, а не украшение.
      */}
      <div className="bg-muted relative hidden lg:block">
        <img
          src="/auth-cover.webp"
          alt="Обложки игр каталога Efira Live: «Горячо-холодно», «Стенка на стенку», «Знаток» и ещё семь"
          className="absolute inset-0 size-full object-cover dark:brightness-[0.75]"
        />
      </div>
    </div>
  );
}
